import { DateTime } from "luxon";

import { CHART_PERIODS, type ChartPeriod, QUALITY_WATCH_ENTITY_TYPES } from "./quality-watch.constants";
import type { QualityWatchChartData, UserAvgDimensionViewResponse } from "./quality-watch.types";

export const getDateRange = (period: ChartPeriod, itemScale: number = 3): string[] => {
    switch (period) {
        case CHART_PERIODS.DAILY:
            return Array.from({ length: 7 * itemScale }, (_, i) => DateTime.now().minus({ days: i }))
                .sort((a, b) => a.toMillis() - b.toMillis())
                .map((date) => date.toFormat("yyyy-MM-dd"));

        case CHART_PERIODS.WEEKLY:
            return Array.from({ length: 3 * itemScale }, (_, i) => DateTime.now().minus({ weeks: i }).startOf("week"))
                .sort((a, b) => a.toMillis() - b.toMillis())
                .map((date) => date.toFormat("MMMM dd"));

        case CHART_PERIODS.MONTHLY:
            return Array.from({ length: 2 * itemScale }, (_, i) => DateTime.now().minus({ months: i }))
                .sort((a, b) => a.toMillis() - b.toMillis())
                .map((date) => date.toFormat("MMMM"));
        default:
            return [];
    }
};

export const getEntityId = (id: string, type: QUALITY_WATCH_ENTITY_TYPES) => {
    switch (type) {
        case QUALITY_WATCH_ENTITY_TYPES.AGENT:
            return `agent-${id}`;
        case QUALITY_WATCH_ENTITY_TYPES.TEAM:
            return `team-${id}`;
        case QUALITY_WATCH_ENTITY_TYPES.BRAND:
            return `brand-${id}`;
    }
};

export const sanitizeChartDataDates = (
    startDate: string,
    endDate: string,
    data: QualityWatchChartData[]
): QualityWatchChartData[] => {
    const start = DateTime.fromISO(startDate);
    const end = DateTime.fromISO(endDate);

    const map = new Map<string, QualityWatchChartData>();
    let currentDate = start;
    while (currentDate <= end) {
        const dateString = currentDate.toFormat("yyyy-MM-dd");
        map.set(dateString, { date: dateString, items: {} });
        currentDate = currentDate.plus({ days: 1 });
    }

    data.forEach((item) => {
        const existingItems = map.get(item.date);
        if (existingItems) {
            Object.entries(item.items).forEach(([key, value]) => {
                existingItems.items[key] = value;
            });
        }
    });

    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// Util to parse backend response to chart data
export const convertUserAvgDimensionViewResponseToChartData = (
    response: UserAvgDimensionViewResponse
): QualityWatchChartData[] => {
    // Group by date first
    const groupedByDate = response.reduce<Record<string, Record<string, number>>>((acc, item) => {
        if (!acc[item.date]) {
            acc[item.date] = {};
        }

        // Create composite key: userId + definitionId
        const entityKey = getEntityId(item.userId, QUALITY_WATCH_ENTITY_TYPES.AGENT);
        const compositeKey = `${entityKey}-${item.definitionId}`;

        acc[item.date][compositeKey] = item.avgValue;

        return acc;
    }, {});

    // Convert to chart data array
    return Object.entries(groupedByDate)
        .map(([date, items]) => ({
            date,
            items,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
};

// Get stroke pattern based on definition index for visual differentiation
export const getStrokeDasharray = (definitionIndex: number): string => {
    const patterns = [
        "0", // Solid line (first definition)
        "5 5", // Dashed line (second definition)
        "2 2", // Dotted line (third definition)
        "10 5 2 5", // Dash-dot line (fourth definition)
        "15 5", // Long dash (fifth definition)
    ];

    return patterns[definitionIndex % patterns.length];
};
